import { DriverOfferBox, MyOffersPanel } from "@/components/price/PriceOffers";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import DriverScheduledRequests from "@/components/price/DriverScheduledRequests";
import WearNegotiationPopup from "@/components/price/WearNegotiationPopup";
import DocumentsRequiredPopup from "@/components/price/DocumentsRequiredPopup";
import DriverDocsBanner from "@/components/price/DriverDocsBanner";

import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Zap, MapPin, Users, Clock, Euro, Menu,
  RadioTower, PlusCircle, Check, Star, Trash2, Loader2,
  Phone, Wallet, CreditCard, Banknote, ChevronRight,
  MessageCircle, Bell, X, Repeat, LifeBuoy, Route as RouteIcon, Send,
} from "lucide-react";
import PriceBottomTabs from "@/components/price/PriceBottomTabs";
import PriceHubPanel from "@/components/price/PriceHubPanel";
import PinnedTripCard from "@/components/price/PinnedTripCard";

const fmtDay = (d) => {
  try { return new Date(`${d}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }); } catch { return d; }
};
import { PriceScooterIcon } from "@/components/price/PriceIllustrations";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PublishTripSheet from "@/components/price/PublishTripSheet";
import TripThreadsSheet, { useTripThreads } from "@/components/price/TripThreadsSheet";
import RateModal from "@/components/price/RateModal";
import RoutePoints from "@/components/price/RoutePoints";
import HistorySheet from "@/components/price/HistorySheet";
import ChatSheet from "@/components/price/ChatSheet";
import NavButtons from "@/components/price/NavButtons";
import PriceProfileSheet from "@/components/price/PriceProfileSheet";
import PriceSurveyPopup from "@/components/price/PriceSurveyPopup";
import AdminPopupCenter from "@/components/price/AdminPopupCenter";
import PriceSupportFAB from "@/components/price/PriceSupportFAB";
import { flushPendingPhoto } from "@/lib/pendingPhoto";
import { isNativeStripeAvailable, topupPriceWalletNative } from "@/lib/stripeNative";

/**
 * Price — Driver dashboard (covoiturage).
 *
 *  1. En ligne / Hors ligne — pilote la réception des demandes live.
 *  2. Demandes de course en direct — poll toutes les 5s. Le premier covoitureur
 *     à cliquer "Accepter" gagne la course. Fois acceptée, la course
 *     bascule dans "Ma course en cours" avec numéro passager + Terminer.
 *  3. Publier un trajet planifié → PublishTripSheet.
 *  4. Mes trajets ouverts + historique (avec notation passager après
 *     complétion).
 */
export default function DriverDashboard() {
  const { user, profile, logout } = useAuth();
  const nav = useNavigate();
  const [online, setOnline] = useState(true);
  const [now, setNow] = useState(new Date());
  const [publishOpen, setPublishOpen] = useState(false);
  const [tripThreadsOpen, setTripThreadsOpen] = useState(false);
  const tripThreads = useTripThreads("driver");
  const [trips, setTrips] = useState([]);
  const [series, setSeries] = useState([]);
  const [liveRequests, setLiveRequests] = useState([]);
  const [activeRide, setActiveRide] = useState(null); // demande live acceptée par ce covoitureur
  const [loading, setLoading] = useState(true);
  const [rateSeat, setRateSeat] = useState(null); // {seat, trip} for scheduled trip pax
  const [rateRide, setRateRide] = useState(null); // ride-request completed to rate
  const [wallet, setWallet] = useState({ cagnotte_balance_eur: 0, wallet_balance_eur: 0, balance_eur: 0, topup_presets_eur: [], commission_pct: 25 });
  const [walletOpen, setWalletOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [busyTopup, setBusyTopup] = useState(false);
  const [radiusKm, setRadiusKm] = useState(25);
  const [radiusPresets, setRadiusPresets] = useState([5, 10, 25, 50, 100]);
  const [profileSheetOpen, setProfileSheetOpen] = useState(false);
  const [tab, setTab] = useState("home"); // home | activity | messages

  // Grab the driver's current GPS position (best-effort — used to bias
  // ride-request broadcasts by proximity). Falls back to null if the
  // user hasn't granted geolocation permission yet.
  const getGeoPosition = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return null;
    return new Promise((resolve) => {
      let done = false;
      const finish = (v) => { if (!done) { done = true; resolve(v); } };
      navigator.geolocation.getCurrentPosition(
        (p) => finish({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => finish(null),
        { enableHighAccuracy: false, timeout: 4000, maximumAge: 60000 },
      );
      // Extra safety net: geolocation callbacks can silently hang on some
      // Android devices, so we cap the wait.
      setTimeout(() => finish(null), 4500);
    });
  }, []);
  // Course en attente d'acceptation après recharge (l'user a cliqué
  // "Accepter" mais son portefeuille était vide → on rouvre le sheet
  // de recharge, et si la recharge passe, on retente l'acceptation).
  const [pendingAcceptReq, setPendingAcceptReq] = useState(null);
  const [pendingCommission, setPendingCommission] = useState(0);

  // Persist active ride so notif clicks don't lose the in-progress course.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const stored = localStorage.getItem("price_active_driver_ride_id");
        if (!stored) return;
        const r = await api.get(`/price/ride-requests/${stored}`);
        const req = r.data?.request;
        if (cancel || !req) return;
        if (["accepted", "passenger_boarding", "in_progress"].includes(req.status)) {
          setActiveRide(req);
        } else {
          localStorage.removeItem("price_active_driver_ride_id");
        }
      } catch (_e) {
        localStorage.removeItem("price_active_driver_ride_id");
      }
    })();
    return () => { cancel = true; };
  }, []);

  // Auto-pickup des courses attribuées par l'admin (ou récupération après
  // un push notif raté / app relancée). Toutes les 5s, on demande au
  // backend s'il existe une course en cours affectée à ce covoitureur.
  // Dès qu'elle apparaît, on la hisse en "Ma course en cours" et on
  // affiche un toast + un petit vibre device si dispo.
  //
  // Polling pausé quand l'onglet est caché (document.hidden) pour éviter
  // les requêtes inutiles en arrière-plan.
  useEffect(() => {
    let cancel = false;
    let timerId = null;
    const poll = async () => {
      if (activeRide?.id) return; // déjà une course en cours
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const r = await api.get("/price/driver/active-ride");
        const req = r.data?.request;
        if (cancel || !req) return;
        setActiveRide(req);
        localStorage.setItem("price_active_driver_ride_id", req.id);
        if (req.assigned_by_admin) {
          toast.success("🎯 Course attribuée par Price — contacte le passager !", { duration: 7000 });
        } else {
          toast.success("Course en cours récupérée");
        }
        try {
          if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate([120, 60, 120]);
        } catch (_e) { /* ignore */ }
      } catch (_e) { /* silencieux */ }
    };
    const onVisibility = () => {
      if (typeof document !== "undefined" && !document.hidden) poll();
    };
    poll();
    timerId = setInterval(poll, 5000);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }
    return () => {
      cancel = true;
      if (timerId) clearInterval(timerId);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
  }, [activeRide?.id]);

  const loadWallet = useCallback(async () => {
    try {
      const r = await api.get("/price/driver/wallet");
      if (r.data) setWallet(r.data);
    } catch (_e) { /* silencieux */ }
  }, []);

  useEffect(() => { loadWallet(); }, [loadWallet]);

  // Si le covoitureur a téléversé une photo lors de l'inscription, on
  // l'envoie discrètement une fois qu'il est loggé (l'endpoint requiert
  // l'auth, donc on ne peut pas le faire au moment du register).
  useEffect(() => { flushPendingPhoto(); }, []);

  // On mount, sync the "En ligne" state to the backend so the passenger
  // ride-request broadcast picks up this driver immediately.
  //
  // ⚠️ We intentionally do NOT flip `online: false` on unmount. React
  // will unmount this component every time the driver navigates to a
  // subroute OR when iOS Capacitor tears the WebView down while the
  // app is backgrounded. That was making every driver drop offline
  // seconds after opening the app → passengers saw "0 covoitureurs".
  //
  // The freshness of the online state is now enforced server-side via
  // `price_online_at` + a TTL in `_price_online_driver_ids_near`. The
  // driver stays online until they toggle off manually, log out, or
  // their last heartbeat is older than the TTL.
  useEffect(() => {
    let cancel = false;
    (async () => {
      // Load the driver's current radius preference (and available presets).
      try {
        const rr = await api.get("/price/driver/radius");
        if (!cancel) {
          setRadiusKm(rr.data?.radius_km ?? 25);
          if (Array.isArray(rr.data?.presets_km) && rr.data.presets_km.length) {
            setRadiusPresets(rr.data.presets_km);
          }
        }
      } catch (_e) { /* silencieux */ }
      // Push initial online + GPS + radius so the passenger broadcast picks
      // this driver up immediately.
      try {
        const geo = await getGeoPosition();
        await api.post("/price/driver/online", {
          online: true,
          ...(geo || {}),
          radius_km: radiusKm,
        });
        if (!cancel) setOnline(true);
      } catch (_e) { if (!cancel) setOnline(false); }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Heartbeat every 2 minutes while the dashboard is mounted so the
  // server-side TTL doesn't tick the driver offline mid-session. We also
  // refresh the GPS position each tick so the broadcast filter stays
  // accurate as the driver moves.
  useEffect(() => {
    if (!online) return undefined;
    const t = setInterval(async () => {
      try {
        const geo = await getGeoPosition();
        api.post("/price/driver/online", {
          online: true,
          ...(geo || {}),
          radius_km: radiusKm,
        }).catch(() => {});
      } catch (_e) { /* noop */ }
    }, 2 * 60 * 1000);
    return () => clearInterval(t);
  }, [online, radiusKm, getGeoPosition]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const firstName =
    profile?.first_name ||
    (profile?.full_name || "").split(" ")[0] ||
    (user?.email || "").split("@")[0] ||
    "Covoitureur";

  const loadTrips = useCallback(async () => {
    try {
      const r = await api.get("/price/trips/mine");
      setTrips(r.data?.trips || []);
      setSeries(r.data?.series || []);
    } catch (_e) { /* silencieux */ } finally {
      setLoading(false);
    }
  }, []);

  const loadLiveRequests = useCallback(async () => {
    if (!online) {
      setLiveRequests([]);
      return;
    }
    try {
      const r = await api.get("/price/ride-requests/available");
      setLiveRequests(r.data?.requests || []);
    } catch (_e) { /* silencieux */ }
  }, [online]);

  useEffect(() => {
    loadTrips();
    const t = setInterval(loadTrips, 20000);
    return () => clearInterval(t);
  }, [loadTrips]);

  // Poll live requests every 5s when online.
  useEffect(() => {
    loadLiveRequests();
    const t = setInterval(loadLiveRequests, 5000);
    return () => clearInterval(t);
  }, [loadLiveRequests]);

  const handleLogout = async () => {
    await logout();
    nav("/", { replace: true });
  };

  const acceptRequest = async (req) => {
    try {
      const r = await api.post(`/price/ride-requests/${req.id}/accept`);
      const accepted = r.data?.request;
      setActiveRide(accepted);
      if (accepted?.id) localStorage.setItem("price_active_driver_ride_id", accepted.id);
      setLiveRequests((cur) => cur.filter((x) => x.id !== req.id));
      setPendingAcceptReq(null);
      setPendingCommission(0);
      toast.success("Course acceptée ! Contacte le passager 📞");
      loadWallet();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || "Course déjà prise";
      if (status === 402) {
        // Portefeuille insuffisant — on calcule la commission attendue et
        // on ouvre directement le sheet de recharge. Après recharge
        // réussie, l'effet ci-dessous relance automatiquement l'accept.
        const pct = Number(wallet.commission_pct || 25);
        const commission = Math.max(
          0.5,
          Math.ceil((Number(req.price_eur || 0) * pct) / 100 * 100) / 100,
        );
        setPendingAcceptReq(req);
        setPendingCommission(commission);
        toast(`Recharge ton portefeuille (${commission.toFixed(2)}€ nécessaire) pour accepter cette course`, { duration: 6000 });
        loadWallet();
        setWalletOpen(true);
      } else {
        toast.error(msg);
        loadLiveRequests();
      }
    }
  };

  const completeRide = async () => {
    if (!activeRide) return;
    try {
      await api.post(`/price/ride-requests/${activeRide.id}/complete`);
      toast.success("Course terminée — note ton passager ⭐");
      const finished = { ...activeRide, status: "completed" };
      localStorage.removeItem("price_active_driver_ride_id");
      setActiveRide(null);
      setRateRide(finished);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const confirmOnboard = async () => {
    if (!activeRide?.id) return;
    try {
      const r = await api.post(`/price/ride-requests/${activeRide.id}/driver-confirm-onboard`);
      if (r.data?.request) setActiveRide(r.data.request);
      toast.success("Passager à bord — course lancée 🚗");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const cancelByDriver = async ({ reason, note }) => {
    if (!activeRide?.id) return;
    try {
      const r = await api.post(`/price/ride-requests/${activeRide.id}/cancel-by-driver`, {
        reason,
        note: note || "",
      });
      toast(r.data?.relaunched
        ? `Course annulée (${r.data?.reason || reason}) — la demande est relancée aux autres covoitureurs`
        : `Course annulée : ${r.data?.reason || reason}`);
      localStorage.removeItem("price_active_driver_ride_id");
      setActiveRide(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  // Poll pour capter les changements de statut du passager
  // (accepted → passenger_boarding notamment).
  useEffect(() => {
    if (!activeRide?.id) return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/price/ride-requests/${activeRide.id}`);
        const req = r.data?.request;
        if (!req) return;
        if (req.status === "cancelled") {
          localStorage.removeItem("price_active_driver_ride_id");
          setActiveRide(null);
          toast("Course annulée par le passager");
          return;
        }
        if (req.status === "completed") {
          localStorage.removeItem("price_active_driver_ride_id");
          setActiveRide(null);
          setRateRide(req);
          return;
        }
        // Sinon on met à jour l'état pour refléter passenger_boarding
        // ou d'autres transitions.
        if (req.status !== activeRide.status) {
          setActiveRide(req);
        }
      } catch (_e) { /* silencieux */ }
    }, 3000);
    return () => clearInterval(t);
  }, [activeRide?.id, activeRide?.status]);

  const topupWallet = async (amountEur) => {
    setBusyTopup(true);
    try {
      // On mobile natif (iOS/Android) : on ouvre Stripe Checkout dans un
      // in-app browser (SFSafariViewController sur iOS). Stripe gère
      // Apple Pay côté leur serveur — pas besoin de merchant iOS.
      // L'utilisateur reste visuellement dans l'app, la sheet retombe
      // dessus après paiement.
      if (isNativeStripeAvailable()) {
        const { Browser } = await import("@capacitor/browser");
        const r = await api.post("/price/driver/wallet/topup", {
          amount_eur: amountEur,
          origin_url: window.location.origin,
        });
        const url = r.data?.checkout_url;
        if (!url) throw new Error("URL Stripe introuvable");
        // Écoute la fermeture du browser (l'user paye → success_url →
        // browser reste ouvert quelques secondes → user tape "Terminé").
        const listener = await Browser.addListener("browserFinished", async () => {
          listener.remove();
          // Le webhook Stripe crédite le wallet — on lui laisse 2s puis
          // on rafraîchit + éventuellement on retente l'acceptation.
          setTimeout(async () => {
            await loadWallet();
            if (pendingAcceptReq) {
              const toAccept = pendingAcceptReq;
              setPendingAcceptReq(null);
              setPendingCommission(0);
              toast("Nouvelle tentative d'acceptation…");
              await acceptRequest(toAccept);
            }
          }, 2000);
          setWalletOpen(false);
        });
        await Browser.open({ url, presentationStyle: "popover" });
        return;
      }
      // Web fallback — Stripe Checkout redirect (same tab).
      const r = await api.post("/price/driver/wallet/topup", {
        amount_eur: amountEur,
        origin_url: window.location.origin,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error("Erreur Stripe");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Erreur Stripe");
    } finally {
      setBusyTopup(false);
    }
  };

  // Retrait wallet → banque via Stripe Connect. Prompt simple pour
  // récupérer le montant. Le back valide (min 5€, payouts_enabled,
  // solde suffisant) et crée un Stripe Transfer.
  const withdrawWallet = async () => {
    const walletBal = Number(wallet?.wallet_balance_eur || 0);
    if (walletBal < 5) {
      toast.error("Minimum de retrait : 5€. Ton portefeuille contient " + walletBal.toFixed(2) + "€.");
      return;
    }
    const raw = window.prompt(
      `Montant à retirer (max ${walletBal.toFixed(2)}€, minimum 5€) :`,
      String(Math.floor(walletBal))
    );
    if (!raw) return;
    const amt = Number(String(raw).replace(",", "."));
    if (!isFinite(amt) || amt < 5) {
      toast.error("Montant invalide (minimum 5€)");
      return;
    }
    if (amt > walletBal) {
      toast.error(`Solde insuffisant (max ${walletBal.toFixed(2)}€)`);
      return;
    }
    if (!window.confirm(`Retirer ${amt.toFixed(2)}€ sur ton compte bancaire ? (1-2 jours ouvrés)`)) return;
    setBusyTopup(true);
    try {
      const r = await api.post("/price/driver/wallet/withdraw", { amount_eur: amt });
      toast.success(r.data?.message || `${amt.toFixed(2)}€ virés · dispo sous 1-2 jours`);
      await loadWallet();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Erreur retrait");
    } finally {
      setBusyTopup(false);
    }
  };

  const completeTrip = async (tripId) => {
    try {
      await api.post(`/price/trips/${tripId}/complete`);
      toast.success("Trajet terminé — pense à noter tes passagers ⭐");
      loadTrips();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const stopSeries = async (s) => {
    if (!window.confirm(`Arrêter la série « ${s.repeat_label} » ${s.from_address} → ${s.to_address} ? Les dates à venir seront annulées et les passagers prévenus.`)) return;
    try {
      const r = await api.post(`/price/trip-series/${s.id}/stop`);
      toast(`Série arrêtée · ${r.data?.cancelled_count || 0} date(s) annulée(s)`);
      loadTrips();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const cancelTrip = async (tripId) => {
    if (!window.confirm("Annuler ce trajet ? Les passagers seront prévenus.")) return;
    try {
      await api.post(`/price/trips/${tripId}/cancel`);
      toast("Trajet annulé");
      loadTrips();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const acceptSeat = async (seat) => {
    try {
      await api.post(`/price/seats/${seat.id}/accept`);
      toast.success(`Réservation acceptée pour ${seat.client_name} ✅`);
      loadTrips();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const refuseSeat = async (seat) => {
    if (!window.confirm(`Refuser la réservation de ${seat.client_name} ?`)) return;
    try {
      await api.post(`/price/seats/${seat.id}/refuse`);
      toast("Réservation refusée");
      loadTrips();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const submitPassengerRating = async ({ stars, comment }) => {
    const seatId = rateSeat?.seat?.id;
    if (!seatId) return;
    await api.post(`/price/seats/${seatId}/rate-client`, { stars, comment });
    setTrips((cur) =>
      cur.map((t) => ({
        ...t,
        seats_booked: (t.seats_booked || []).map((s) =>
          s.id === seatId ? { ...s, rated_by_driver: true } : s
        ),
      }))
    );
  };

  const submitLiveRating = async ({ stars, comment }) => {
    if (!rateRide?.id) return;
    await api.post(`/price/ride-requests/${rateRide.id}/rate-client`, { stars, comment });
  };

  const timeStr = now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

  // Trajets ouverts triés par date ; pour une série on n'affiche que la
  // prochaine date + celles qui ont déjà des réservations.
  const { openTrips, hiddenBySeries } = useMemo(() => {
    const open = trips
      .filter((t) => t.status === "open")
      .sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`));
    const shown = new Set();
    const hidden = {};
    const list = open.filter((t) => {
      if (!t.series_id) return true;
      const booked = (t.seats_booked || []).some((s) => !["cancelled", "refused"].includes(s.status));
      if (booked || !shown.has(t.series_id)) { shown.add(t.series_id); return true; }
      hidden[t.series_id] = (hidden[t.series_id] || 0) + 1;
      return false;
    });
    return { openTrips: list, hiddenBySeries: hidden };
  }, [trips]);
  const otherTrips = useMemo(() => trips.filter((t) => t.status !== "open"), [trips]);
  const activeSeats = (t) => (t.seats_booked || []).filter((s) => !["cancelled", "refused"].includes(s.status));
  const _n = new Date();
  const nowKey = `${_n.getFullYear()}-${String(_n.getMonth() + 1).padStart(2, "0")}-${String(_n.getDate()).padStart(2, "0")} ${_n.toTimeString().slice(0, 5)}`;
  const pinnedTrip = openTrips.find((t) => `${t.date} ${t.time}` >= nowKey && activeSeats(t).length > 0) || null;
  const pendingSeatCount = openTrips.reduce((n, t) => n + (t.seats_booked || []).filter((s) => s.status === "pending").length, 0);
  const [reqTab, setReqTab] = useState("instant");
  useEffect(() => {
    const off = () => setOnline(false);
    window.addEventListener("price:force-offline", off);
    return () => window.removeEventListener("price:force-offline", off);
  }, []);
  const toggleOnline = async () => {
    const next = !online;
    setOnline(next);
    try {
      const geo = next ? await getGeoPosition() : null;
      await api.post("/price/driver/online", { online: next, ...(geo || {}), radius_km: radiusKm });
      toast.success(next ? "Tu es en ligne !" : "Tu es hors ligne");
    } catch (err) {
      setOnline(!next);
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  // Course active — écran plein-page qui remplace toute la home du
  // covoitureur (photo passager, itinéraire, boutons de confirmation
  // et d'annulation). On garde le rateRide + sheets accessibles pour
  // la clôture. On overlaie aussi les demandes live entrantes pour que
  // le covoitureur reste informé de la demande pendant qu'il roule.
  // On monte TOUJOURS AdminPopupCenter — sinon les popups admin (ex.
  // "Dis-nous ta ville") n'arrivent jamais chez les covoitureurs qui
  // ont une course en cours. Mount coûte ~rien car il poll /pending.
  if (activeRide) {
    return (
      <>
        <ActiveRideScreen
          ride={activeRide}
          onConfirmOnboard={confirmOnboard}
          onComplete={completeRide}
          onCancel={cancelByDriver}
        />
        <IncomingRequestsOverlay requests={liveRequests} />
        <WearNegotiationPopup />
        {tripThreads.unread > 0 && (
          <button
            onClick={() => setTripThreadsOpen(true)}
            className="street-press fixed right-4 z-40 inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-2 text-[12px] font-black street-border-thin street-shadow-sm"
            style={{ bottom: "max(96px, calc(env(safe-area-inset-bottom) + 96px))" }}
            data-testid="driver-active-trip-messages"
          >
            <MessageCircle className="h-4 w-4" /> {tripThreads.unread} msg passagers
          </button>
        )}
        <TripThreadsSheet open={tripThreadsOpen} onClose={() => setTripThreadsOpen(false)} role="driver" threads={tripThreads} />
        {rateRide && (
          <RateModal
            title={`Note ${rateRide.client_name || "le passager"}`}
            open={!!rateRide}
            onClose={() => setRateRide(null)}
            onSubmit={submitLiveRating}
          />
        )}
        <AdminPopupCenter
          onActionTarget={(target) => {
            if (target === "wallet") { loadWallet(); setWalletOpen(true); }
            else if (target === "history") { setHistoryOpen(true); }
            else { setProfileSheetOpen(true); }
          }}
        />
      </>
    );
  }

  return (
    <div
      className="relative min-h-screen text-foreground street-dots"
      style={{ background: "hsl(15 100% 96%)" }}
      data-testid="driver-dashboard"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, hsla(38, 95%, 62%, 0.42), transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-24 bottom-48 h-72 w-72 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, hsla(354, 88%, 66%, 0.30), transparent 70%)" }}
      />

      <div
        className="relative mx-auto flex min-h-screen w-full max-w-md flex-col"
        style={{
          paddingTop: "max(20px, env(safe-area-inset-top))",
          paddingBottom: "calc(110px + env(safe-area-inset-bottom))",
          paddingLeft: "max(20px, env(safe-area-inset-left))",
          paddingRight: "max(20px, env(safe-area-inset-right))",
        }}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="relative h-11 w-11 rounded-2xl bg-primary grid place-items-center rotate-[-6deg] street-border street-shadow-sm">
              <span className="font-[Outfit] text-lg font-black text-primary-foreground">P</span>
              <span className="absolute -right-1.5 -bottom-1.5 h-3 w-3 rounded-full bg-accent street-border-thin" />
            </div>
            <div>
              <div className="font-[Outfit] text-lg font-black leading-none">Price</div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-foreground/50">Covoitureur</div>
            </div>
          </div>
          <button
            onClick={() => setProfileSheetOpen(true)}
            className="street-press inline-flex h-10 w-10 items-center justify-center rounded-full bg-white street-border-thin street-shadow-sm"
            aria-label="Mon compte"
            data-testid="driver-menu-open"
          >
            <Menu className="h-4 w-4 text-foreground" strokeWidth={2.75} />
          </button>
        </div>

        {tab === "messages" && (
          <PriceHubPanel
            title="Messages"
            subtitle="Tes échanges avec les passagers et l'équipe Price."
            testid="driver-messages-panel"
            items={[
              { testid: "driver-trip-messages-open", label: "Messages passagers", sub: "Questions sur tes trajets publiés", icon: <MessageCircle className="h-5 w-5" strokeWidth={2.5} />, badge: tripThreads.unread, onClick: () => setTripThreadsOpen(true) },
              { testid: "driver-messages-support", label: "Support Price", sub: "Une question ? L'équipe te répond", icon: <LifeBuoy className="h-5 w-5" strokeWidth={2.5} />, tone: "bg-white", onClick: () => window.dispatchEvent(new Event("price:open-support")) },
            ]}
          />
        )}

        {tab === "activity" && (
          <div className="mt-6 animate-in fade-in slide-in-from-bottom-2 duration-300" data-testid="driver-activity-panel">
            <h1 className="font-[Outfit] text-[28px] font-black leading-tight">Activité</h1>
            <p className="mt-1 text-[13px] font-medium text-foreground/65">Tes soldes, trajets publiés et réservations.</p>
          </div>
        )}

        {tab === "home" && (<>
        {/* Greeting */}
        <div className="mt-6">
          <p className="text-sm font-bold uppercase tracking-widest text-foreground/50">
            {timeStr} · Salut
          </p>
          <h1 className="mt-1 font-[Outfit] text-[32px] font-black leading-[1.02] tracking-tight">
            {firstName} 👋
          </h1>
        </div>

        <DriverDocsBanner onOpen={() => setProfileSheetOpen(true)} />

        {pinnedTrip && (
          <PinnedTripCard
            testid="driver-pinned-trip"
            kicker="Ton prochain trajet"
            title={`${activeSeats(pinnedTrip).length} réservation${activeSeats(pinnedTrip).length > 1 ? "s" : ""}`}
            from={pinnedTrip.from_address}
            to={pinnedTrip.to_address}
            meta={`${fmtDay(pinnedTrip.date)} · ${pinnedTrip.time}`}
            action="Gérer"
            onClick={() => { setTab("activity"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          />
        )}

        {/* En ligne + Publier — 2 grandes tuiles (proposition B) */}
        <div className="mt-4 grid grid-cols-2 gap-3" data-testid="driver-home-tiles">
          <button
            type="button"
            onClick={toggleOnline}
            className={`street-press relative flex h-32 flex-col justify-between overflow-hidden rounded-3xl p-4 text-left street-border street-shadow ${online ? "bg-emerald-400" : "bg-white"}`}
            data-testid="driver-online-toggle"
          >
            <span className={`relative inline-flex h-7 w-12 items-center rounded-full street-border-thin transition-colors ${online ? "bg-white" : "bg-muted"}`}>
              <span className={`absolute h-5 w-5 rounded-full street-border-thin transition-transform ${online ? "translate-x-[22px] bg-emerald-500" : "translate-x-[2px] bg-white"}`} />
            </span>
            <span>
              <span className="block font-[Outfit] text-[19px] font-black leading-none">{online ? "En ligne" : "Hors ligne"}</span>
              <span className="mt-1 block text-[11px] font-bold opacity-75">{online ? "Tu reçois les demandes" : "Touche pour te connecter"}</span>
            </span>
            {online && <RadioTower className="absolute right-3 top-3 h-5 w-5 animate-pulse" strokeWidth={2.75} />}
          </button>
          <button
            type="button"
            onClick={() => setPublishOpen(true)}
            className="street-press relative flex h-32 flex-col justify-between overflow-hidden rounded-3xl bg-primary p-4 text-left text-primary-foreground street-border street-shadow"
            data-testid="driver-publish"
          >
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-white street-border-thin">
              <Send className="h-4 w-4 text-primary" strokeWidth={2.75} />
            </span>
            <span>
              <span className="block font-[Outfit] text-[19px] font-black leading-none">Publier un trajet</span>
              <span className="mt-1 block text-[11px] font-bold text-primary-foreground/85">30 secondes</span>
            </span>
          </button>
        </div>

        <div className={online ? "mt-3 rounded-3xl bg-white p-4 street-border-thin street-shadow-sm" : "hidden"}>
          {/* Rayon d'acceptation — chaque covoitureur choisit la distance
              maximale à laquelle il veut recevoir les demandes live. */}
          {online && (
            <div data-testid="driver-radius-picker">
              <div className="flex items-center justify-between mb-2">
                <span className="font-[Outfit] text-[12px] font-black uppercase tracking-wider text-foreground/70">
                  Rayon d'acceptation
                </span>
                <span className="font-[Outfit] text-[13px] font-black text-primary">
                  {radiusKm} km
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {radiusPresets.map((km) => (
                  <button
                    key={km}
                    type="button"
                    data-testid={`radius-preset-${km}`}
                    onClick={async () => {
                      const prev = radiusKm;
                      setRadiusKm(km);
                      try {
                        await api.put("/price/driver/radius", { radius_km: km });
                        // Push refreshed heartbeat so the change kicks in
                        // immediately without waiting for the 2 min tick.
                        const geo = await getGeoPosition();
                        api.post("/price/driver/online", {
                          online: true,
                          ...(geo || {}),
                          radius_km: km,
                        }).catch(() => {});
                        toast.success(`Rayon fixé à ${km} km`);
                      } catch (e) {
                        setRadiusKm(prev);
                        toast.error(e?.response?.data?.detail || "Erreur");
                      }
                    }}
                    className={
                      "street-press h-9 min-w-[52px] px-3 rounded-full font-[Outfit] text-[12px] font-black street-border-thin " +
                      (radiusKm === km
                        ? "bg-primary text-primary-foreground shadow-[0_2px_0_0_#000]"
                        : "bg-white text-foreground")
                    }
                  >
                    {km}km
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[11px] font-medium text-foreground/60 leading-snug">
                Tu ne reçois que les demandes dont le point de départ est à moins de {radiusKm} km de ta position.
              </p>
            </div>
          )}
        </div>
        </>)}

        {tab === "activity" && (<>
        {/* Portefeuille — 2 pots distincts. Cagnotte (non retirable)
            couvre les commissions cash, wallet (retirable) accumule les
            gains cartes. Tap = ouvre le sheet complet. */}
        <button
          onClick={() => { loadWallet(); setWalletOpen(true); }}
          className="street-press mt-4 w-full rounded-3xl bg-white p-3 street-border street-shadow overflow-hidden"
          data-testid="wallet-open"
        >
          <div className="flex items-center gap-2 px-1 mb-2">
            <div className="grid h-8 w-8 flex-none place-items-center rounded-xl bg-accent street-border-thin">
              <Wallet className="h-4 w-4 text-accent-foreground" strokeWidth={2.5} />
            </div>
            <div className="flex-1 text-left">
              <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50 leading-none">
                Mes soldes
              </div>
              <div className="text-[10px] font-medium text-foreground/50 mt-0.5">
                Commission {Number(wallet.commission_pct || 25).toFixed(0)}% · Tap pour gérer
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-foreground/40" strokeWidth={2.5} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-2xl bg-primary p-2.5 text-left">
              <div className="text-[9px] font-black uppercase tracking-wider text-primary-foreground/80 leading-none">
                🐷 Cagnotte
              </div>
              <div className="mt-1 font-[Outfit] text-[19px] font-black leading-none tabular-nums text-primary-foreground">
                {Number(wallet.cagnotte_balance_eur ?? wallet.balance_eur ?? 0).toFixed(2)}€
              </div>
              <div className="mt-1 text-[9px] font-bold text-primary-foreground/75 uppercase tracking-wider">
                Non retirable
              </div>
            </div>
            <div className="rounded-2xl bg-emerald-500 p-2.5 text-left">
              <div className="text-[9px] font-black uppercase tracking-wider text-white/85 leading-none">
                💳 Portefeuille
              </div>
              <div className="mt-1 font-[Outfit] text-[19px] font-black leading-none tabular-nums text-white">
                {Number(wallet.wallet_balance_eur || 0).toFixed(2)}€
              </div>
              <div className="mt-1 text-[9px] font-bold text-white/85 uppercase tracking-wider">
                Retirable
              </div>
            </div>
          </div>
          {Number(wallet.pending_earnings_eur || 0) > 0 && (
            <div
              className="mt-2 rounded-xl bg-amber-100 px-3 py-2 flex items-center justify-between gap-2 street-border-thin"
              data-testid="driver-pending-earnings"
            >
              <div className="min-w-0">
                <div className="text-[9px] font-black uppercase tracking-widest text-amber-900/80 leading-none">
                  ⏳ En attente de versement
                </div>
                <div className="mt-0.5 text-[10px] text-amber-900/70 truncate">
                  {wallet.pending_rides_count || 0}&nbsp;course
                  {(wallet.pending_rides_count || 0) > 1 ? "s" : ""} payée
                  {(wallet.pending_rides_count || 0) > 1 ? "s" : ""} · dispo sous
                  2-7j
                </div>
              </div>
              <div className="font-[Outfit] text-[18px] font-black tabular-nums text-amber-900 flex-none">
                +{Number(wallet.pending_earnings_eur || 0).toFixed(2)}€
              </div>
            </div>
          )}
        </button>

        <button
          onClick={() => setHistoryOpen(true)}
          className="street-press mt-3 flex w-full items-center gap-3 rounded-3xl bg-white p-4 text-left street-border-thin street-shadow-sm"
          data-testid="driver-activity-history"
        >
          <div className="grid h-10 w-10 flex-none place-items-center rounded-2xl bg-[#FFE9D6] street-border-thin">
            <RouteIcon className="h-5 w-5" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-[Outfit] text-[15px] font-black leading-tight">Historique des courses</div>
            <div className="text-[12px] font-medium text-foreground/60">Toutes tes courses terminées</div>
          </div>
          <ChevronRight className="h-5 w-5 text-foreground/40" />
        </button>
        </>)}

        <WearNegotiationPopup />
        <DocumentsRequiredPopup role="driver" />

        {tab === "home" && (<>
        {/* Demandes — onglets Instantanées / Programmées (proposition B) */}
        {!activeRide && (
          <div className="mt-6" data-testid="driver-requests-section">
            <h2 className="font-[Outfit] text-[20px] font-black leading-tight">Demandes</h2>
            <div className="mt-3 grid grid-cols-2 gap-1 rounded-full bg-white p-1 street-border-thin" role="tablist">
              {[["instant", "Instantanées"], ["scheduled", "Programmées"]].map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setReqTab(k)}
                  className={`h-9 rounded-full font-[Outfit] text-[13px] font-black transition-colors ${reqTab === k ? "bg-primary text-primary-foreground street-border-thin" : "text-foreground/60"}`}
                  data-testid={`driver-requests-tab-${k}`}
                >
                  {k === "instant" && liveRequests.length > 0 ? `${l} (${liveRequests.length})` : l}
                </button>
              ))}
            </div>
            <MyOffersPanel me="driver" />
            {reqTab === "scheduled" && <DriverScheduledRequests />}
            {reqTab === "instant" && (
              <div className="mt-1" data-testid="driver-live-requests">
                {!online && <EmptyState message="Tu es hors ligne. Passe en ligne pour recevoir les demandes en direct." />}
                {online && liveRequests.length === 0 && (
                  <EmptyState message="Aucune demande pour l'instant. Reste connecté, on te prévient dès qu'un passager cherche 🔔" />
                )}
                {online && liveRequests.map((r) => (
                  <LiveRequestCard key={r.id} req={r} onAccept={() => acceptRequest(r)} onOfferDone={(d) => {
                    if (d?.request?.status === "accepted") { setActiveRide(d.request); localStorage.setItem("price_active_driver_ride_id", d.request.id); loadWallet(); }
                    loadLiveRequests();
                  }} />
                ))}
              </div>
            )}
          </div>
        )}
        </>)}

        {tab === "activity" && (<>
        {/* Mes trajets ouverts */}
        <div className="mt-6 flex items-center justify-between">
          <h2 className="font-[Outfit] text-[15px] font-black uppercase tracking-widest text-foreground/70">
            Mes trajets ouverts
          </h2>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-foreground/40" />}
        </div>

        {series.map((s) => (
          <div key={s.id} className="mt-3 flex items-center gap-3 rounded-2xl bg-accent/40 p-3 street-border-thin" data-testid={`driver-series-${s.id}`}>
            <Repeat className="h-5 w-5 flex-none" strokeWidth={2.5} />
            <div className="min-w-0 flex-1">
              <div className="font-[Outfit] text-[13px] font-black">{s.repeat_label} · {s.time}</div>
              <div className="truncate text-[11px] font-bold text-foreground/60">{s.from_address} → {s.to_address}</div>
              {hiddenBySeries[s.id] > 0 && (
                <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50" data-testid={`driver-series-more-${s.id}`}>
                  + {hiddenBySeries[s.id]} autre{hiddenBySeries[s.id] > 1 ? "s" : ""} date{hiddenBySeries[s.id] > 1 ? "s" : ""} sans réservation
                </div>
              )}
            </div>
            <button onClick={() => stopSeries(s)} className="street-press rounded-xl bg-white px-3 py-2 text-[11px] font-black street-border-thin" data-testid={`driver-series-stop-${s.id}`}>
              Arrêter
            </button>
          </div>
        ))}

        {!loading && openTrips.length === 0 && (
          <EmptyState message="Aucun trajet publié pour le moment. Publie ton premier trajet !" />
        )}

        {openTrips.map((t) => (
          <TripCard
            key={t.id}
            trip={t}
            onComplete={() => completeTrip(t.id)}
            onCancel={() => cancelTrip(t.id)}
            onRatePassenger={(seat) => setRateSeat({ seat, trip: t })}
            onAcceptSeat={acceptSeat}
            onRefuseSeat={refuseSeat}
          />
        ))}

        {otherTrips.length > 0 && (
          <>
            <div className="mt-6 flex items-center justify-between">
              <h2 className="font-[Outfit] text-[15px] font-black uppercase tracking-widest text-foreground/70">
                Historique
              </h2>
            </div>
            {otherTrips.map((t) => (
              <TripCard
                key={t.id}
                trip={t}
                readonly
                onRatePassenger={(seat) => setRateSeat({ seat, trip: t })}
              />
            ))}
          </>
        )}
        </>)}

        <div className="mt-8 text-center text-[11px] font-bold uppercase tracking-widest text-foreground/40">
          Price · Covoiturage
        </div>
      </div>

      <PriceBottomTabs
        prefix="driver"
        active={profileSheetOpen ? "settings" : tab}
        badges={{ activity: pendingSeatCount, messages: tripThreads.unread }}
        onChange={(k) => {
          if (k === "settings") { setProfileSheetOpen(true); return; }
          setProfileSheetOpen(false);
          if (k === "activity") { loadTrips(); loadWallet(); }
          setTab(k);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
      <TripThreadsSheet open={tripThreadsOpen} onClose={() => setTripThreadsOpen(false)} role="driver" threads={tripThreads} />

      <PublishTripSheet
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        onPublished={loadTrips}
        defaultVehicleType={profile?.price_vehicle_kind || "car"}
      />

      <WalletSheet
        open={walletOpen}
        onClose={() => { setWalletOpen(false); setPendingAcceptReq(null); setPendingCommission(0); }}
        wallet={wallet}
        onTopup={topupWallet}
        onWithdraw={withdrawWallet}
        busy={busyTopup}
        pendingReq={pendingAcceptReq}
        pendingCommission={pendingCommission}
      />

      <RateModal
        open={!!rateSeat}
        onClose={() => setRateSeat(null)}
        onSubmit={submitPassengerRating}
        title="Noter ton passager"
        subtitle={
          rateSeat
            ? `${rateSeat.seat.client_name} · ${rateSeat.trip.from_address} → ${rateSeat.trip.to_address}`
            : ""
        }
        target_label="passager"
      />

      <RateModal
        open={!!rateRide}
        onClose={() => setRateRide(null)}
        onSubmit={submitLiveRating}
        title="Noter ton passager"
        subtitle={
          rateRide
            ? `${rateRide.client_name} · ${rateRide.from_address} → ${rateRide.to_address}`
            : ""
        }
        target_label="passager"
      />

      <HistorySheet open={historyOpen} onClose={() => setHistoryOpen(false)} />

      <PriceProfileSheet
        open={profileSheetOpen}
        onClose={() => setProfileSheetOpen(false)}
        variant="driver"
        onLogout={async () => { setProfileSheetOpen(false); await handleLogout(); }}
        onHistory={() => setHistoryOpen(true)}
        displayName={profile ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() : "Covoitureur"}
        displayEmail={user?.email || profile?.email}
      />

      {/* Popup "Question de Price" — admin broadcast surveys */}
      <PriceSurveyPopup />

      <AdminPopupCenter
        onActionTarget={(target) => {
          // Le driver a cliqué l'action ; on route vers le sheet
          // concerné. `stripe_connect`, `profile_photo` et `profile`
          // ouvrent tous le PriceProfileSheet (variant driver contient
          // la carte Stripe). `wallet` ouvre le sheet portefeuille.
          if (target === "wallet") { loadWallet(); setWalletOpen(true); }
          else if (target === "history") { setHistoryOpen(true); }
          else { setProfileSheetOpen(true); }
        }}
      />

      <PriceSupportFAB />
    </div>
  );
}

function IncomingRequestsOverlay({ requests }) {
  const [open, setOpen] = useState(false);
  const count = Array.isArray(requests) ? requests.length : 0;
  if (count === 0) return null;

  return (
    <>
      {/* Pastille flottante — s'affiche en permanence pendant une course
          active pour signaler qu'il y a des demandes en attente. On ne
          propose PAS d'accepter directement : la logique de commission +
          swap d'active ride serait cassante. Le covoitureur peut au
          moins consulter la file et prévoir sa suite. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="incoming-requests-fab"
        aria-label={`${count} demande${count > 1 ? "s" : ""} en attente`}
        className="street-press fixed z-40 right-4 flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2.5 street-border street-shadow"
        style={{
          bottom: "calc(env(safe-area-inset-bottom) + 200px)",
        }}
      >
        <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
          <Bell className="h-4 w-4" strokeWidth={2.8} />
          <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-accent text-accent-foreground text-[10px] font-black street-border-thin px-1">
            {count > 9 ? "9+" : count}
          </span>
        </span>
        <span className="font-[Outfit] text-[13px] font-black uppercase tracking-widest">
          {count === 1 ? "1 demande" : `${count} demandes`}
        </span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center sm:justify-center"
          onClick={() => setOpen(false)}
          data-testid="incoming-requests-sheet"
        >
          <div
            className="w-full sm:max-w-md max-h-[80dvh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-[#FFF8F1] p-4 street-border street-shadow-lg flex flex-col"
            style={{
              paddingBottom: "max(16px, calc(env(safe-area-inset-bottom) + 12px))",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-[Outfit] text-[16px] font-black leading-none">
                  Demandes en attente
                </div>
                <div className="mt-1 text-[11px] font-bold uppercase tracking-widest text-foreground/50">
                  {count} demande{count > 1 ? "s" : ""} disponible{count > 1 ? "s" : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Fermer"
                data-testid="incoming-requests-close"
                className="street-press h-9 w-9 rounded-full bg-white grid place-items-center street-border-thin street-shadow-sm"
              >
                <X className="h-4 w-4" strokeWidth={3} />
              </button>
            </div>

            {/* Info claire : on est en course, on ne peut pas prendre une
                nouvelle course avant d'avoir terminé la courante. */}
            <div className="mt-3 rounded-2xl bg-white p-3 street-border-thin flex items-start gap-2">
              <span className="text-[18px] leading-none">ℹ️</span>
              <div className="text-[12px] font-medium text-foreground/70 leading-snug">
                Tu es en course actuellement. <b>Termine ta course</b> pour
                pouvoir accepter une de ces demandes — la première acceptée
                sera pour toi.
              </div>
            </div>

            <div className="mt-3 space-y-3">
              {requests.map((req) => (
                <IncomingRequestPreview key={req.id} req={req} />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function IncomingRequestPreview({ req }) {
  const isCash = (req.payment_method || "cash") === "cash";
  return (
    <div
      className="rounded-2xl bg-white p-3 street-border-thin street-shadow-sm"
      data-testid={`incoming-preview-${req.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-[Outfit] text-[14px] font-black truncate">
            {req.client_name}
          </div>
          <div className="mt-0.5 text-[10px] font-bold uppercase tracking-widest text-foreground/50">
            {req.seats} passager{req.seats > 1 ? "s" : ""} · {isCash ? "Espèces" : "Carte"}
          </div>
        </div>
        <div className="text-right">
          <div className="font-[Outfit] text-[18px] font-black text-primary leading-none">
            {Number(req.price_eur).toFixed(2)}€
          </div>
        </div>
      </div>
      <RoutePoints from={req.from_address} to={req.to_address} size="sm" className="mt-3" />
    </div>
  );
}

function LiveRequestCard({ req, onAccept, onOfferDone }) {
  const isCash = (req.payment_method || "cash") === "cash";
  const isTwoWheels = req.accept_two_wheels === true;
  const helmet = req.passenger_has_helmet;
  const hasHelmetInfo = isTwoWheels && (helmet === true || helmet === false);
  return (
    <div
      className="mt-3 rounded-3xl bg-white p-5 street-border street-shadow animate-fade-up"
      data-testid={`live-request-${req.id}`}
    >
      {/* GROS bandeau casque — pour les courses 2 roues, l'info doit être
          visible en premier. Le motard décide s'il accepte selon casque. */}
      {hasHelmetInfo && (
        <div
          className={
            "-mx-2 -mt-2 mb-3 flex items-center gap-2 rounded-2xl px-3 py-2.5 street-border-thin " +
            (helmet ? "bg-emerald-400" : "bg-amber-400")
          }
          data-testid={`live-request-helmet-${req.id}`}
        >
          <span className="text-[24px] leading-none">{helmet ? "✅" : "⚠️"}</span>
          <div className="min-w-0 flex-1">
            <div className="font-[Outfit] text-[14px] font-black uppercase tracking-wider text-black leading-tight">
              {helmet ? "Passager avec casque" : "Passager SANS casque"}
            </div>
            {!helmet && (
              <div className="text-[11px] font-black text-black/80 leading-tight mt-0.5">
                Prévois-en un ou refuse la course
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-[Outfit] text-[15px] font-black leading-tight truncate">
            {req.client_name}
          </div>
          <div className="mt-0.5 text-[11px] font-bold uppercase tracking-widest text-foreground/50">
            {req.seats} passager{req.seats > 1 ? "s" : ""}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <div className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest street-border-thin ${
              isCash ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground"
            }`}>
              {isCash ? <Banknote className="h-3 w-3" /> : <CreditCard className="h-3 w-3" />}
              {isCash ? "Espèces" : "Carte"}
            </div>
            {isTwoWheels && (
              <div className="inline-flex items-center gap-1 rounded-full bg-[#FFF3CC] px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-black street-border-thin">
                <PriceScooterIcon size={12} />
                2 roues OK
              </div>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="font-[Outfit] text-[22px] font-black text-primary leading-none">
            {Number(req.negotiable && req.passenger_proposed_eur ? req.passenger_proposed_eur : req.price_eur).toFixed(2)}€
          </div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-foreground/50">
            {req.negotiable ? (req.passenger_proposed_eur ? "Prix du passager" : "Prix recommandé") : "Course"}
          </div>
        </div>
      </div>

      {req.pickup_eta_min != null && (
        <div className="mt-4 flex items-center gap-3 rounded-2xl bg-[#FFF3CC] px-3 py-2.5 street-border-thin" data-testid={`live-request-eta-${req.id}`}>
          <Clock className="h-5 w-5 flex-none" strokeWidth={2.75} />
          <div className="min-w-0 flex-1">
            <div className="font-[Outfit] text-[18px] font-black leading-none">{req.pickup_eta_min} min</div>
            <div className="mt-0.5 text-[11px] font-bold text-foreground/70">pour rejoindre le passager · {req.pickup_distance_km} km</div>
          </div>
        </div>
      )}
      {req.relaunch_count > 0 && (
        <div className="mt-2 inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-primary" data-testid={`live-request-relaunched-${req.id}`}>
          Relancée · un covoitureur a annulé
        </div>
      )}

      <RoutePoints from={req.from_address} to={req.to_address} size="sm" className="mt-4" />

      {req.notes && (
        <div className="mt-3 rounded-xl bg-secondary/40 p-2 text-[12px] font-medium text-foreground/70 street-border-thin">
          {req.notes}
        </div>
      )}

      {req.negotiable ? <DriverOfferBox req={req} onSent={onOfferDone} /> : (
        <button
          onClick={onAccept}
          className="street-press mt-4 w-full rounded-2xl bg-primary py-3 font-[Outfit] text-[15px] font-black text-primary-foreground street-border street-shadow"
          data-testid={`accept-request-${req.id}`}
        >
          Accepter la course 🚀
        </button>
      )}
    </div>
  );
}

const DRIVER_CANCEL_REASONS = [
  { code: "drunk", emoji: "🍺", label: "Passager en état d'ivresse" },
  { code: "no_show", emoji: "⏱️", label: "Passager introuvable / ne répond pas" },
  { code: "wrong_address", emoji: "📍", label: "Adresse incorrecte / trop loin" },
  { code: "vehicle_issue", emoji: "🚗", label: "Problème véhicule / panne" },
  { code: "sick", emoji: "😷", label: "Passager malade / risque de vomi" },
  { code: "other", emoji: "✏️", label: "Autre raison" },
];

function ActiveRideScreen({ ride, onConfirmOnboard, onComplete, onCancel }) {
  const [chatOpen, setChatOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState(null);
  const [cancelNote, setCancelNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [unread, setUnread] = useState(0);
  const status = ride?.status || "accepted";

  // Badge "messages non lus" sur le bouton Message — polling discret
  // toutes les 10 s tant que le chat est fermé. Pausé onglet caché.
  useEffect(() => {
    if (!ride?.id || chatOpen) { setUnread(0); return; }
    let cancel = false;
    const fetchUnread = async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const r = await api.get(`/price/ride-requests/${ride.id}/messages/unread-count`);
        if (cancel) return;
        const n = Number(r.data?.count || 0);
        setUnread((prev) => {
          if (n > prev && n > 0) {
            try {
              if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(45);
            } catch (_e) { /* ignore */ }
          }
          return n;
        });
      } catch (_e) { /* silencieux */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 10000);
    return () => { cancel = true; clearInterval(t); };
  }, [ride?.id, chatOpen]);

  const priceEur = Number(ride?.price_eur || 0);
  const commission = Number(ride?.commission_eur || 0);
  const clientInitial = (ride?.client_name || "?").slice(0, 1).toUpperCase();
  const clientRating = Number(ride?.client_rating || 0);
  const clientRides = Number(ride?.client_rides_count || 0);

  const doConfirm = async () => {
    setBusy(true);
    try { await onConfirmOnboard(); } finally { setBusy(false); }
  };
  const doComplete = async () => {
    setBusy(true);
    try { await onComplete(); } finally { setBusy(false); }
  };
  const doCancel = async () => {
    if (!cancelReason) return;
    setBusy(true);
    try {
      await onCancel({ reason: cancelReason, note: cancelNote });
      setCancelOpen(false);
    } finally { setBusy(false); }
  };

  // Configuration visuelle par statut.
  const statusMeta = {
    accepted: {
      badge: "Passager en attente",
      badgeColor: "bg-accent",
      badgeDot: "bg-primary",
      title: "En route vers le passager",
      subtitle: "Prends contact et récupère-le au point de rendez-vous.",
    },
    passenger_boarding: {
      badge: "Passager à bord",
      badgeColor: "bg-emerald-300",
      badgeDot: "bg-emerald-900",
      title: `${ride?.client_name || "Le passager"} est monté`,
      subtitle: "Confirme pour démarrer la course officiellement.",
    },
    in_progress: {
      badge: "Course en cours",
      badgeColor: "bg-primary",
      badgeDot: "bg-white",
      title: "Roule vers la destination 🚗",
      subtitle: "Termine la course une fois le passager déposé.",
    },
  }[status] || {
    badge: "Course active",
    badgeColor: "bg-accent",
    badgeDot: "bg-primary",
    title: "Course en cours",
    subtitle: "",
  };

  return (
    <div
      className="relative min-h-screen text-foreground street-dots"
      style={{ background: "hsl(15 100% 96%)" }}
      data-testid="driver-active-ride-screen"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, hsla(38, 95%, 62%, 0.42), transparent 70%)" }}
      />
      <div
        className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col"
        style={{
          paddingTop: "max(20px, env(safe-area-inset-top))",
          paddingBottom: "max(24px, env(safe-area-inset-bottom))",
          paddingLeft: "max(20px, env(safe-area-inset-left))",
          paddingRight: "max(20px, env(safe-area-inset-right))",
        }}
      >
        <div className={`inline-flex w-fit items-center gap-1.5 rounded-full ${statusMeta.badgeColor} px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-foreground street-border-thin street-shadow-sm`}>
          <span className={`h-1.5 w-1.5 rounded-full ${statusMeta.badgeDot} animate-ping`} />
          {statusMeta.badge}
        </div>

        <h1 className="mt-4 font-[Outfit] text-[26px] font-black leading-tight" data-testid="driver-active-title">
          {statusMeta.title}
        </h1>
        {statusMeta.subtitle && (
          <p className="mt-1 text-[13px] font-medium text-foreground/70">
            {statusMeta.subtitle}
          </p>
        )}

        {/* Fiche passager */}
        <div className="mt-5 rounded-3xl bg-white p-5 street-border street-shadow">
          <div className="flex items-center gap-4">
            {ride?.client_photo_url ? (
              <img
                src={ride.client_photo_url}
                alt={ride.client_name || "Passager"}
                className="h-20 w-20 flex-none rounded-2xl object-cover street-border-thin street-shadow-sm"
                data-testid="driver-active-client-photo"
              />
            ) : (
              <div className="grid h-20 w-20 flex-none place-items-center rounded-2xl bg-accent text-accent-foreground street-border-thin street-shadow-sm">
                <span className="font-[Outfit] text-[30px] font-black">{clientInitial}</span>
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-[Outfit] text-[20px] font-black leading-tight truncate" data-testid="driver-active-client-name">
                {ride?.client_name || "Passager"}
              </div>
              <div className="mt-1 flex items-center gap-1 text-[13px] font-bold text-foreground/70">
                <Star className="h-4 w-4 text-primary fill-primary" strokeWidth={2.5} />
                {clientRating > 0 ? clientRating.toFixed(1).replace(".", ",") : "Nouveau"}
                {clientRides > 0 && (
                  <span className="text-foreground/40">· {clientRides} course{clientRides > 1 ? "s" : ""}</span>
                )}
              </div>
              <div className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-bold text-foreground/60">
                <Users className="h-3.5 w-3.5" /> {ride?.seats || 1} place{(ride?.seats || 1) > 1 ? "s" : ""}
              </div>
            </div>
          </div>

          {/* Casque — visible en gros pour les courses 2 roues */}
          {ride.accept_two_wheels && (ride.passenger_has_helmet === true || ride.passenger_has_helmet === false) && (
            <div
              className={
                "mt-4 flex items-center gap-2 rounded-2xl px-3 py-2.5 street-border-thin " +
                (ride.passenger_has_helmet ? "bg-emerald-400" : "bg-amber-400")
              }
              data-testid="active-ride-helmet"
            >
              <span className="text-[22px] leading-none">{ride.passenger_has_helmet ? "✅" : "⚠️"}</span>
              <div className="min-w-0 flex-1">
                <div className="font-[Outfit] text-[13px] font-black uppercase tracking-wider text-black leading-tight">
                  {ride.passenger_has_helmet ? "Passager avec casque" : "Passager SANS casque"}
                </div>
                {!ride.passenger_has_helmet && (
                  <div className="text-[11px] font-black text-black/80 leading-tight mt-0.5">
                    Prévois-en un ou refuse la course
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contact rapide */}
          <div className="mt-4 grid grid-cols-2 gap-2">
            {ride?.client_phone ? (
              <a
                href={`tel:${ride.client_phone}`}
                className="street-press inline-flex items-center justify-center gap-1.5 rounded-2xl bg-primary py-3 font-[Outfit] text-[13px] font-black text-primary-foreground street-border-thin street-shadow-sm"
                data-testid="driver-active-call"
              >
                <Phone className="h-4 w-4" strokeWidth={2.5} /> Appeler
              </a>
            ) : (
              <div className="inline-flex items-center justify-center rounded-2xl bg-white/60 py-3 text-[11px] font-medium text-foreground/50 street-border-thin">—</div>
            )}
            <button
              type="button"
              onClick={() => setChatOpen(true)}
              className="street-press relative inline-flex items-center justify-center gap-1.5 rounded-2xl bg-white py-3 font-[Outfit] text-[13px] font-black street-border-thin street-shadow-sm"
              data-testid="driver-active-chat"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={2.5} /> Message
              {unread > 0 && (
                <span
                  className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] px-1 grid place-items-center rounded-full bg-primary text-primary-foreground text-[11px] font-black street-border-thin animate-pulse"
                  data-testid="driver-chat-unread-badge"
                >
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Itinéraire + navigation */}
        <div className="mt-4 rounded-3xl bg-white p-5 street-border street-shadow">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-black uppercase tracking-widest text-foreground/50">
              Itinéraire
            </div>
            <div className="font-[Outfit] text-[18px] font-black text-primary tabular-nums">
              {Number(priceEur).toFixed(2).replace(".", ",")} €
            </div>
          </div>
          <div className="mt-3">
            <RoutePoints
              from={ride?.from_address}
              to={ride?.to_address}
              size="sm"
              className="flex-1"
            />
          </div>
          {ride?.from_coord?.lat != null && status !== "in_progress" && (
            <div className="mt-3 flex items-center justify-between gap-2 rounded-2xl bg-accent/40 p-3 street-border-thin">
              <div className="min-w-0">
                <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60">
                  Aller vers le passager
                </div>
                <div className="mt-0.5 truncate font-[Outfit] text-[13px] font-bold text-foreground">
                  {ride.from_address}
                </div>
              </div>
              <NavButtons
                lat={ride.from_coord.lat}
                lng={ride.from_coord.lng}
                label={ride.from_address}
                variant="compact"
                testid="driver-active-nav-pickup"
              />
            </div>
          )}
          {ride?.to_coord?.lat != null && status === "in_progress" && (
            <div className="mt-3 flex items-center justify-between gap-2 rounded-2xl bg-emerald-100 p-3 street-border-thin">
              <div className="min-w-0">
                <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60">
                  Destination finale
                </div>
                <div className="mt-0.5 truncate font-[Outfit] text-[13px] font-bold text-foreground">
                  {ride.to_address}
                </div>
              </div>
              <NavButtons
                lat={ride.to_coord.lat}
                lng={ride.to_coord.lng}
                label={ride.to_address}
                variant="compact"
                testid="driver-active-nav-drop"
              />
            </div>
          )}
          {ride?.payment_method === "cash" && commission > 0 && (
            <div className="mt-3 flex items-center justify-between border-t-2 border-black/10 pt-2 text-[12px] font-bold">
              <span className="text-foreground/60">Commission Price</span>
              <span className="tabular-nums text-foreground/60">− {commission.toFixed(2).replace(".", ",")} €</span>
            </div>
          )}
        </div>

        {/* CTA principal selon le statut */}
        {status === "accepted" && (
          <div
            className="mt-5 rounded-2xl bg-amber-100 p-4 text-center street-border street-shadow"
            data-testid="driver-waiting-passenger"
          >
            <div className="font-[Outfit] text-[14px] font-black text-amber-900">
              ⏳ En attente du passager
            </div>
            <p className="mt-1 text-[12px] font-medium text-amber-800/80 leading-snug">
              {ride?.client_name || "Le passager"} doit confirmer qu'il est dans le véhicule.
            </p>
          </div>
        )}

        {status === "passenger_boarding" && (
          <button
            type="button"
            onClick={doConfirm}
            disabled={busy}
            className="street-press-lg mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 py-4 font-[Outfit] text-[15px] font-black uppercase tracking-wider text-white street-border street-shadow disabled:opacity-60"
            data-testid="driver-confirm-onboard"
          >
            <Check className="h-5 w-5" strokeWidth={3} />
            {busy ? "…" : "Passager à bord — Démarrer"}
          </button>
        )}

        {status === "in_progress" && (
          <button
            type="button"
            onClick={doComplete}
            disabled={busy}
            className="street-press-lg mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-4 font-[Outfit] text-[15px] font-black uppercase tracking-wider text-primary-foreground street-border street-shadow disabled:opacity-60"
            data-testid="driver-complete-ride"
          >
            <Check className="h-5 w-5" strokeWidth={3} />
            {busy ? "…" : "Terminer la course"}
          </button>
        )}

        {/* Bouton annulation — toujours disponible tant que la course
            n'est pas complétée. */}
        <button
          type="button"
          onClick={() => { setCancelReason(null); setCancelNote(""); setCancelOpen(true); }}
          className="street-press mt-3 flex w-full items-center justify-center gap-1.5 rounded-2xl bg-white py-3 font-[Outfit] text-[13px] font-black text-foreground/70 street-border-thin street-shadow-sm"
          data-testid="driver-cancel-open"
        >
          Annuler la course
        </button>

        <div className="mt-auto pt-8 text-center text-[11px] font-bold uppercase tracking-widest text-foreground/40">
          Price · Covoiturage
        </div>
      </div>

      <ChatSheet
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        requestId={ride?.id}
        selfRole="driver"
        counterpartName={ride?.client_name || "Passager"}
      />

      {/* Sheet — annulation avec raison */}
      {cancelOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:p-4" data-testid="driver-cancel-sheet">
          <div className="w-full max-w-md rounded-t-3xl sm:rounded-3xl bg-white p-5 street-border street-shadow max-h-[92vh] overflow-y-auto">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="text-[11px] font-black uppercase tracking-widest text-foreground/50">
                  Annuler la course
                </div>
                <div className="mt-1 font-[Outfit] text-[18px] font-black">
                  Choisis une raison
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCancelOpen(false)}
                className="street-press grid h-9 w-9 place-items-center rounded-full bg-white street-border-thin"
                aria-label="Fermer"
                data-testid="driver-cancel-close"
              >
                ×
              </button>
            </div>

            <div className="space-y-2">
              {DRIVER_CANCEL_REASONS.map((r) => (
                <button
                  key={r.code}
                  type="button"
                  onClick={() => setCancelReason(r.code)}
                  className={
                    "w-full flex items-center gap-3 rounded-2xl px-3 py-3 text-left street-border-thin " +
                    (cancelReason === r.code ? "bg-primary text-primary-foreground street-shadow-sm" : "bg-white")
                  }
                  data-testid={`driver-cancel-reason-${r.code}`}
                >
                  <span className="text-[22px] leading-none">{r.emoji}</span>
                  <span className="font-[Outfit] text-[13px] font-black">{r.label}</span>
                </button>
              ))}
            </div>

            {cancelReason === "other" && (
              <textarea
                value={cancelNote}
                onChange={(e) => setCancelNote(e.target.value)}
                placeholder="Explique brièvement…"
                rows={3}
                className="mt-3 w-full rounded-2xl border-2 border-black bg-white p-3 text-[14px] font-medium outline-none focus:ring-4 focus:ring-primary/25"
                data-testid="driver-cancel-note"
              />
            )}

            <button
              type="button"
              onClick={doCancel}
              disabled={!cancelReason || busy}
              className="street-press-lg mt-4 flex w-full items-center justify-center rounded-2xl bg-red-500 py-3.5 font-[Outfit] text-[14px] font-black uppercase tracking-wider text-white street-border street-shadow disabled:opacity-50"
              data-testid="driver-cancel-submit"
            >
              {busy ? "…" : "Confirmer l'annulation"}
            </button>
            <button
              type="button"
              onClick={() => setCancelOpen(false)}
              className="mt-2 w-full py-2 text-[12px] font-black uppercase tracking-widest text-foreground/50"
              data-testid="driver-cancel-back"
            >
              Retour
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TripCard({ trip, onComplete, onCancel, onRatePassenger, onAcceptSeat, onRefuseSeat, readonly = false }) {
  const dateLabel = formatFrDate(trip.date);
  const isCompleted = trip.status === "completed";
  const isCancelled = trip.status === "cancelled";
  const seatsBooked = trip.seats_booked || [];
  const confirmedPassengers = seatsBooked.filter((s) => !["cancelled", "refused"].includes(s.status));
  const completedForRating = seatsBooked.filter(
    (s) => s.status === "completed" && !s.rated_by_driver
  );

  // Terminer visible seulement quand la course a démarré (date+heure de
  // départ passée). Avant l'heure du départ, on ne montre pas Terminer
  // pour éviter les fins prématurées.
  const tripStarted = React.useMemo(() => {
    try {
      const dt = new Date(`${trip.date}T${trip.time || "00:00"}:00`);
      return Date.now() >= dt.getTime();
    } catch {
      return false;
    }
  }, [trip.date, trip.time]);

  return (
    <div
      className={`mt-3 rounded-3xl bg-white p-5 street-border street-shadow ${
        isCancelled ? "opacity-60" : ""
      }`}
      data-testid={`trip-card-${trip.id}`}
    >
      <div className="flex items-start gap-3">
        <RoutePoints from={trip.from_address} to={trip.to_address} className="flex-1" />
        <StatusPill status={trip.status} />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Badge icon={<Clock className="h-3.5 w-3.5" />}>{dateLabel} · {trip.time}</Badge>
        <Badge icon={<Users className="h-3.5 w-3.5" />}>
          {trip.seats_total - (trip.seats_available ?? trip.seats_total)}/{trip.seats_total} pris
        </Badge>
        <Badge icon={<Euro className="h-3.5 w-3.5" />} highlight>
          {Number(trip.price_per_seat_eur).toFixed(2)}€ / place
        </Badge>
        {trip.repeat_label && <Badge icon={<Repeat className="h-3.5 w-3.5" />}>{trip.repeat_label}</Badge>}
        {(trip.vehicle_type === "motorcycle" || trip.vehicle_type === "scooter") && (
          <Badge icon={<PriceScooterIcon size={14} />}>
            {trip.vehicle_type === "motorcycle" ? "Moto" : "Scooter"} · 2 roues
          </Badge>
        )}
      </div>

      {trip.notes && (
        <div className="mt-3 rounded-xl bg-secondary/40 p-2 text-[12px] font-medium text-foreground/70 street-border-thin">
          {trip.notes}
        </div>
      )}

      {confirmedPassengers.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50">
            Passagers
          </div>
          {confirmedPassengers.map((s) => {
            const isTwoWheels = s.vehicle_type === "motorcycle" || s.vehicle_type === "scooter";
            const hasHelmetInfo = isTwoWheels && (s.has_helmet === true || s.has_helmet === false);
            return (
              <div
                key={s.id}
                className={
                  "rounded-2xl street-border-thin overflow-hidden " +
                  (hasHelmetInfo && !s.has_helmet ? "bg-[#FFF3CC]" : "bg-secondary/30")
                }
                data-testid={`trip-passenger-${s.id}`}
              >
                {hasHelmetInfo && (
                  <div
                    className={
                      "flex items-center gap-2 px-3 py-2 border-b-2 border-black " +
                      (s.has_helmet ? "bg-emerald-400" : "bg-amber-400")
                    }
                    data-testid={`trip-passenger-helmet-${s.id}`}
                  >
                    <span className="text-[22px] leading-none">{s.has_helmet ? "✅" : "⚠️"}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-[Outfit] text-[14px] font-black uppercase tracking-wider text-black leading-tight">
                        {s.has_helmet ? "Passager avec casque" : "Passager SANS casque"}
                      </div>
                      {!s.has_helmet && (
                        <div className="text-[11px] font-black text-black/80 leading-tight mt-0.5">
                          Prévois-en un ou refuse la course
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between gap-2 p-2.5">
                  <div className="min-w-0">
                    <div className="font-[Outfit] text-[13px] font-black leading-tight truncate">
                      {s.client_name} · {s.seats} place{s.seats > 1 ? "s" : ""}
                    </div>
                    <div className="text-[11px] font-medium text-foreground/60">
                      Total {Number(s.total_price_eur).toFixed(2)}€
                      {s.status === "pending" && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest street-border-thin">
                          à valider
                        </span>
                      )}
                    </div>
                  </div>
                  {s.status === "pending" && !readonly && (
                    <div className="flex flex-none gap-1.5">
                      <button
                        onClick={() => onRefuseSeat?.(s)}
                        className="street-press inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-black uppercase tracking-widest text-foreground street-border-thin"
                        data-testid={`seat-refuse-${s.id}`}
                      >
                        <Trash2 className="h-3 w-3" /> Refuser
                      </button>
                      <button
                        onClick={() => onAcceptSeat?.(s)}
                        className="street-press inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-primary-foreground street-border-thin"
                        data-testid={`seat-accept-${s.id}`}
                      >
                        <Check className="h-3 w-3" /> Accepter
                      </button>
                    </div>
                  )}
                  {s.status === "completed" && !s.rated_by_driver && (
                    <button
                      onClick={() => onRatePassenger?.(s)}
                      className="street-press inline-flex items-center gap-1 rounded-full bg-accent px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-accent-foreground street-border-thin"
                      data-testid={`trip-rate-passenger-${s.id}`}
                    >
                      <Star className="h-3.5 w-3.5" strokeWidth={2.5} /> Noter
                    </button>
                  )}
                  {s.rated_by_driver && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-black text-foreground/60">
                      <Check className="h-3.5 w-3.5" /> Noté
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!readonly && !isCancelled && !isCompleted && (
        <div className={"mt-4 grid gap-2 " + (tripStarted ? "grid-cols-2" : "grid-cols-1")}>
          <button
            onClick={onCancel}
            className="street-press inline-flex items-center justify-center gap-1 rounded-2xl bg-white py-3 text-[13px] font-black street-border-thin street-shadow-sm"
            data-testid={`trip-cancel-${trip.id}`}
          >
            <Trash2 className="h-4 w-4" /> Annuler
          </button>
          {tripStarted ? (
            <button
              onClick={onComplete}
              className="street-press inline-flex items-center justify-center gap-1 rounded-2xl bg-primary py-3 text-[13px] font-black text-primary-foreground street-border-thin street-shadow-sm"
              data-testid={`trip-complete-${trip.id}`}
            >
              <Check className="h-4 w-4" /> Terminer
            </button>
          ) : (
            <div
              className="rounded-2xl bg-secondary/40 px-3 py-2 text-center text-[11px] font-medium text-foreground/60 street-border-thin"
              data-testid={`trip-waiting-departure-${trip.id}`}
            >
              Le bouton « Terminer » apparaîtra le jour du départ ({dateLabel} à {trip.time})
            </div>
          )}
        </div>
      )}

      {completedForRating.length > 0 && readonly && (
        <div className="mt-3 text-[11px] font-medium text-foreground/60">
          {completedForRating.length} passager{completedForRating.length > 1 ? "s" : ""} à noter ⭐
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    open: { label: "Ouvert", bg: "bg-accent", fg: "text-accent-foreground" },
    completed: { label: "Terminé", bg: "bg-primary", fg: "text-primary-foreground" },
    cancelled: { label: "Annulé", bg: "bg-muted", fg: "text-foreground/60" },
  };
  const v = map[status] || map.open;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest street-border-thin ${v.bg} ${v.fg}`}
    >
      {v.label}
    </span>
  );
}

function Badge({ icon, children, highlight }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-black street-border-thin ${
        highlight
          ? "bg-accent text-accent-foreground"
          : "bg-secondary/60 text-foreground"
      }`}
    >
      {icon}
      {children}
    </span>
  );
}

function EmptyState({ message }) {
  return (
    <div className="mt-3 rounded-3xl bg-white p-5 text-center text-[13px] font-medium text-foreground/60 street-border-thin street-shadow-sm">
      {message}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Wallet sheet — solde + boutons de recharge (redirige vers Stripe)   */
/* ------------------------------------------------------------------ */
function WalletSheet({ open, onClose, wallet, onTopup, onWithdraw, busy, pendingReq, pendingCommission }) {
  const [customAmount, setCustomAmount] = React.useState("");
  if (!open) return null;
  const presets = Array.isArray(wallet?.topup_presets_eur) && wallet.topup_presets_eur.length
    ? wallet.topup_presets_eur
    : [5, 10, 20, 50, 100];
  const cagnotte = Number(wallet?.cagnotte_balance_eur ?? wallet?.balance_eur ?? 0);
  const walletBal = Number(wallet?.wallet_balance_eur ?? 0);
  const suggestedAmount = pendingCommission
    ? presets.find((p) => p >= pendingCommission) || Math.ceil(pendingCommission)
    : null;
  const submitCustom = () => {
    const n = Number(customAmount);
    if (!isFinite(n) || n < 1) {
      toast.error("Montant minimum 1€");
      return;
    }
    onTopup(Number(n.toFixed(2)));
  };
  return (
    <div
      className="fixed inset-0 z-[120] flex justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="wallet-sheet"
    >
      <div
        className="relative w-full max-w-md overflow-y-auto bg-white street-border street-shadow"
        style={{
          borderRadius: "24px 24px 0 0",
          marginTop: "auto",
          maxHeight: "92vh",
          paddingBottom: "max(24px, env(safe-area-inset-bottom))",
        }}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-5 py-4 street-border-thin"
             style={{ borderTop: 0, borderLeft: 0, borderRight: 0 }}>
          <div className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-accent street-border-thin">
              <Wallet className="h-4 w-4 text-accent-foreground" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-[Outfit] text-[16px] font-black leading-none">Mes soldes</div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-foreground/50">
                Commission {Number(wallet?.commission_pct || 25).toFixed(0)}% Price
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="street-press grid h-9 w-9 place-items-center rounded-full bg-white street-border-thin street-shadow-sm"
            aria-label="Fermer"
            data-testid="wallet-close"
          >
            <span className="sr-only">Fermer</span>
            <span aria-hidden>×</span>
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          {/* CARTE 1 — CAGNOTTE (non retirable) */}
          <div
            className="rounded-2xl bg-primary p-4 street-border street-shadow-sm relative overflow-hidden"
            data-testid="wallet-cagnotte-card"
          >
            <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/10 blur-xl pointer-events-none" />
            <div className="flex items-center justify-between mb-1 relative">
              <div className="text-[10px] font-black uppercase tracking-widest text-primary-foreground/85">
                🐷 Cagnotte
              </div>
              <span className="text-[9px] font-black uppercase tracking-wide bg-white/20 text-primary-foreground px-1.5 py-0.5 rounded-full border border-white/30">
                Non retirable
              </span>
            </div>
            <div className="font-[Outfit] text-[36px] font-black leading-none tabular-nums text-primary-foreground relative">
              {cagnotte.toFixed(2)}€
            </div>
            <div className="mt-2 text-[11px] font-medium text-primary-foreground/85 relative">
              Sert à payer la commission Price sur tes courses en <span className="font-black">espèces</span>. Alimentée par tes recharges, parrainages et bonus admin.
            </div>
          </div>

          {/* CARTE 2 — WALLET (retirable) */}
          <div
            className="rounded-2xl bg-emerald-500 p-4 street-border street-shadow-sm relative overflow-hidden"
            data-testid="wallet-earnings-card"
          >
            <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/10 blur-xl pointer-events-none" />
            <div className="flex items-center justify-between mb-1 relative">
              <div className="text-[10px] font-black uppercase tracking-widest text-white/90">
                💳 Portefeuille
              </div>
              <span className="text-[9px] font-black uppercase tracking-wide bg-white/20 text-white px-1.5 py-0.5 rounded-full border border-white/30">
                Retirable
              </span>
            </div>
            <div className="font-[Outfit] text-[36px] font-black leading-none tabular-nums text-white relative">
              {walletBal.toFixed(2)}€
            </div>
            <div className="mt-2 text-[11px] font-medium text-white/90 relative">
              Tes gains issus des paiements par <span className="font-black">carte bancaire</span>.
            </div>
            {/* Bouton Retirer — visible dès qu'il y a assez pour retirer */}
            <button
              type="button"
              onClick={() => onWithdraw?.()}
              disabled={busy || walletBal < 5}
              data-testid="wallet-withdraw"
              className="street-press mt-3 w-full rounded-xl bg-white text-emerald-700 border-2 border-black py-2.5 font-[Outfit] text-[13px] font-black flex items-center justify-center gap-1.5 shadow-[0_2px_0_0_#000] active:translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed relative"
            >
              <Banknote className="h-4 w-4" strokeWidth={2.75} />
              {walletBal < 5 ? "Minimum 5€ pour retirer" : `Retirer sur mon compte`}
            </button>
            {walletBal >= 5 && (
              <div className="mt-1.5 text-[10px] font-medium text-white/75 text-center relative">
                Virement Stripe · 1-2 jours ouvrés · sans frais
              </div>
            )}
          </div>

          {pendingReq && (
            <div className="rounded-2xl bg-accent p-4 street-border street-shadow-sm animate-fade-up">
              <div className="flex items-center gap-2">
                <span className="grid h-8 w-8 place-items-center rounded-xl bg-white street-border-thin">
                  <Zap className="h-4 w-4 text-accent-foreground" strokeWidth={2.5} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-[Outfit] text-[13px] font-black leading-tight">
                    Course en attente
                  </div>
                  <div className="text-[11px] font-medium text-accent-foreground/80 truncate">
                    {pendingReq.from_address} → {pendingReq.to_address}
                  </div>
                </div>
              </div>
              <div className="mt-2 text-[12px] font-bold text-accent-foreground/90">
                Recharge au moins <span className="font-black">{Number(pendingCommission).toFixed(2)}€</span> dans ta cagnotte pour accepter cette course. Elle démarrera automatiquement après paiement.
              </div>
              {suggestedAmount && (
                <button
                  type="button"
                  onClick={() => onTopup(Number(suggestedAmount))}
                  disabled={busy}
                  className="street-press mt-3 w-full rounded-2xl bg-primary py-3 font-[Outfit] text-[14px] font-black text-primary-foreground street-border street-shadow disabled:opacity-60"
                  data-testid="wallet-topup-suggested"
                >
                  Recharger {suggestedAmount}€ et accepter 🚀
                </button>
              )}
            </div>
          )}

          <div>
            <div className="text-[11px] font-black uppercase tracking-widest text-foreground/50">
              Recharger ma cagnotte
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {presets.map((amt) => (
                <button
                  key={amt}
                  onClick={() => onTopup(amt)}
                  disabled={busy}
                  className="street-press rounded-2xl bg-accent py-3 font-[Outfit] text-[15px] font-black text-accent-foreground street-border-thin street-shadow-sm disabled:opacity-50"
                  data-testid={`wallet-topup-${amt}`}
                >
                  {amt}€
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] font-black uppercase tracking-widest text-foreground/50">
              Montant libre
            </div>
            <div className="mt-2 flex gap-2">
              <input
                type="number"
                min="1"
                step="0.5"
                placeholder="15,00"
                inputMode="decimal"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                className="flex-1 rounded-2xl bg-white px-4 py-3 font-[Outfit] text-[15px] font-black tabular-nums street-border-thin focus:outline-none"
                style={{ fontSize: "16px" }}
                data-testid="wallet-custom-amount"
              />
              <button
                onClick={submitCustom}
                disabled={busy}
                className="street-press rounded-2xl bg-primary px-5 py-3 font-[Outfit] text-[13px] font-black text-primary-foreground street-border-thin street-shadow-sm disabled:opacity-50"
                data-testid="wallet-topup-custom"
              >
                Recharger
              </button>
            </div>
          </div>

          {Array.isArray(wallet?.ledger) && wallet.ledger.length > 0 && (
            <div>
              <div className="text-[11px] font-black uppercase tracking-widest text-foreground/50">
                Dernières opérations
              </div>
              <div className="mt-2 space-y-1.5">
                {wallet.ledger.slice(0, 10).map((l) => (
                  <div
                    key={l.id}
                    className="flex items-center justify-between gap-2 rounded-xl bg-secondary/30 p-2.5 text-[12px] street-border-thin"
                  >
                    <div className="min-w-0">
                      <div className="font-black truncate">
                        {ledgerLabel(l.kind)}
                      </div>
                      <div className="text-[10px] font-medium text-foreground/50">
                        {new Date(l.created_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    <div className={`font-[Outfit] font-black tabular-nums ${
                      Number(l.amount_eur) >= 0 ? "text-emerald-700" : "text-primary"
                    }`}>
                      {Number(l.amount_eur) >= 0 ? "+" : ""}{Number(l.amount_eur).toFixed(2)}€
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ledgerLabel(kind) {
  switch (kind) {
    case "topup_success": return "Recharge cagnotte (carte)";
    case "topup_pending": return "Recharge en attente";
    case "commission_debit": return "Commission course cash";
    case "commission_refund_cancel": return "Remboursement (annulation)";
    case "admin_adjustment": return "Ajustement admin";
    case "referral_bonus": return "Bonus parrainage";
    case "creator_code_bonus": return "Bonus code créateur";
    default: return kind || "Opération";
  }
}

function formatFrDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
  } catch (_e) {
    return iso;
  }
}
